<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import { useAlert } from 'dashboard/composables';
import validations, { getLabelTitleErrorMessage } from './validations';
import { getRandomColor } from 'dashboard/helper/labelColor';
import { LABEL_CATEGORIES } from 'dashboard/constants/labelCategories';
import { useVuelidate } from '@vuelidate/core';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
    OnClickOutside,
    EmojiInput: defineAsyncComponent(
      () => import('shared/components/emoji/EmojiInput.vue')
    ),
  },
  props: {
    prefillTitle: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate(), categoryOptions: LABEL_CATEGORIES };
  },
  data() {
    return {
      color: '#000',
      description: '',
      title: '',
      showOnSidebar: true,
      icon: '',
      categories: [],
      isEmojiPickerOpen: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    labelTitleErrorMessage() {
      const errorMessage = getLabelTitleErrorMessage(this.v$);
      return this.$t(errorMessage);
    },
  },
  mounted() {
    this.color = getRandomColor();
    this.title = this.prefillTitle.toLowerCase();
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onClickInsertEmoji(emoji) {
      this.icon = emoji;
      this.isEmojiPickerOpen = false;
    },
    toggleCategory(key) {
      this.categories = this.categories.includes(key)
        ? this.categories.filter(c => c !== key)
        : [...this.categories, key];
    },
    async addLabel() {
      try {
        await this.$store.dispatch('labels/create', {
          color: this.color,
          description: this.description,
          title: this.title.toLowerCase(),
          show_on_sidebar: this.showOnSidebar,
          icon: this.icon,
          categories: this.categories,
        });
        useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const errorMessage =
          error.message || this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(errorMessage);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('LABEL_MGMT.ADD.TITLE')"
      :header-content="$t('LABEL_MGMT.ADD.DESC')"
    />
    <form class="flex flex-wrap mx-0" @submit.prevent="addLabel">
      <woot-input
        v-model="title"
        :class="{ error: v$.title.$error }"
        class="w-full label-name--input"
        :label="$t('LABEL_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="labelTitleErrorMessage"
        data-testid="label-title"
        @input="v$.title.$touch"
        @blur="v$.title.$touch"
      />

      <woot-input
        v-model="description"
        :class="{ error: v$.description.$error }"
        class="w-full"
        :label="$t('LABEL_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('LABEL_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
        data-testid="label-description"
        @input="v$.description.$touch"
        @blur="v$.description.$touch"
      />

      <div class="w-full">
        <label>
          {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          <woot-color-picker v-model="color" />
        </label>
      </div>

      <div class="w-full">
        <label class="block mb-1">{{ $t('LABEL_MGMT.FORM.ICON.LABEL') }}</label>
        <OnClickOutside @trigger="isEmojiPickerOpen = false">
          <div class="relative inline-block">
            <NextButton
              type="button"
              faded
              slate
              :label="icon || $t('LABEL_MGMT.FORM.ICON.PLACEHOLDER')"
              :icon="icon ? '' : 'i-lucide-smile-plus'"
              @click.prevent="isEmojiPickerOpen = !isEmojiPickerOpen"
            />
            <EmojiInput
              v-if="isEmojiPickerOpen"
              class="absolute left-0 z-50 top-10"
              show-remove-button
              :on-click="onClickInsertEmoji"
            />
          </div>
        </OnClickOutside>
      </div>

      <div class="w-full">
        <label class="block mb-1">
          {{ $t('LABEL_MGMT.CATEGORIES.LABEL') }}
        </label>
        <p class="mb-2 text-xs text-n-slate-11">
          {{ $t('LABEL_MGMT.CATEGORIES.HELP') }}
        </p>
        <div class="flex flex-wrap gap-x-4 gap-y-2">
          <label
            v-for="option in categoryOptions"
            :key="option.key"
            class="flex items-center gap-2 mb-0 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="categories.includes(option.key)"
              @change="toggleCategory(option.key)"
            />
            <span>{{ $t(option.i18nKey) }}</span>
          </label>
        </div>
      </div>

      <div class="flex items-center w-full gap-2">
        <input v-model="showOnSidebar" type="checkbox" :value="true" />
        <label for="conversation_creation">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </label>
      </div>
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('LABEL_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          data-testid="label-submit"
          :label="$t('LABEL_MGMT.FORM.CREATE')"
          :disabled="v$.title.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  :deep(input) {
    @apply lowercase;
  }
}
</style>
